"use client";

import { capitalize } from "lodash";
import { AlertCircle, CircleCheck } from "lucide-react";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";

import ExchangeImage from "@/components/global/exchange-swticher/exchangeIcon";
import LoadingSpinner from "@/components/loading-spinner/loading-spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { UnlockButton } from "@/components/unlockButton";
import { Link } from "@/lib/i18n";
import { mixpanelExchange } from "@/lib/tracker";
import * as m from "@/paraglide/messages";
import { useAuth } from "@/services/context/auth/AuthContext";
import { addPaperAccount } from "@/services/controllers/exchanges";
import { fetchConnectedKeys } from "@/services/hoc/client.wrapper";
import { useAppSelector } from "@/services/hooks/redux.hooks";

export default function AddExchangeContent({
  dialogForm = false,
}: {
  dialogForm?: boolean;
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { providers, connectedKeys } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { subscription } = useAppSelector((state) => state.user);

  const [selectedExchange, setSelectedExchange] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");
  const [paperAccountName, setPaperAccountName] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [open, setIsOpen] = useState<boolean>(false);

  const handleExchangeSelect = useCallback((exchange: string) => {
    setSelectedExchange(exchange);
  }, []);

  const filteredProviders = useMemo(
    () =>
      providers.filter((e) =>
        e.name.toLowerCase().includes(keyword.toLowerCase()),
      ),
    [keyword, providers],
  );

  const onAddPaperAccount = useCallback(() => {
    setIsAdding(true);
    addPaperAccount({
      data: {
        name: paperAccountName,
      },
    })
      .then(() => {
        if (user) mixpanelExchange(user?.uid, paperAccountName, "add");
        toast({
          title: m.exchange_status(),
          description: m.paper_acc_added({
            paperAccountName: paperAccountName,
          }),
          variant: "success",
          duration: 2000,
        });
        fetchConnectedKeys();
        setIsAdding(false);
        setIsOpen(false);
      })
      .catch(() => {
        setIsAdding(false);
      });
  }, [paperAccountName, user, toast]);

  const hasPaper = useMemo(
    () => connectedKeys.filter((e) => e.account_type === "paper").length > 0,
    [connectedKeys],
  );
  const hasExchange = useMemo(
    () =>
      connectedKeys.filter(
        (e) => e.account_type !== "paper" && e.exchange_key_id != null,
      ).length > 0,
    [connectedKeys],
  );

  if (dialogForm) {
    return (
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{m.orange_honest_ladybug_believe()}</DialogTitle>
          <DialogDescription>{m.novel_bland_hornet_leap()}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Input
            type="search"
            placeholder={m.search_for_exchange()}
            className="focus-visible:ring-0"
            onChange={(e) => setKeyword(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-auto">
            {!providers.length ? (
              [...Array(5)].map(() => (
                <Skeleton className="w-full h-[40px]" key={Math.random()} />
              ))
            ) : !filteredProviders.length ? (
              <p className="text-center my-4 text-sm col-span-2">
                {m.cozy_teal_tortoise_roam()} <b>{keyword}</b>
              </p>
            ) : (
              filteredProviders.map((provider) => {
                const isActive = selectedExchange === provider.name;
                return (
                  <Button
                    key={provider.id}
                    variant="outline"
                    className={`relative flex items-center justify-center space-x-2 ${
                      isActive ? "border-primary" : ""
                    }`}
                    onClick={() => handleExchangeSelect(provider.name)}
                  >
                    <ExchangeImage providerId={provider.id} width={24} />
                    <span>{capitalize(provider.name)}</span>
                    {isActive && (
                      <CircleCheck className="absolute right-2 top-1/2 -translate-y-1/2" />
                    )}
                  </Button>
                );
              })
            )}
          </div>
          <p className="text-center text-muted-foreground text-sm">
            {m.topical_major_camel_dream()}
          </p>
          <div className="flex justify-center">
            <Dialog open={open} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center justify-center space-x-2 md:w-1/2 self-center"
                >
                  <Image
                    src="/paper.svg"
                    alt="Paper Account Icon"
                    width={24}
                    height={24}
                  />
                  <span>{m.lime_crisp_llama_talk()}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{m.patchy_crazy_gazelle_boil()}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <Label
                    htmlFor="portfolio-name"
                    className="text-left min-w-fit"
                  >
                    {m.portfolio_name()}
                  </Label>
                  <Input
                    id="portfolio-name"
                    placeholder={m.soft_watery_fish_breathe()}
                    className="focus-visible:ring-0"
                    value={paperAccountName}
                    onChange={(e) => setPaperAccountName(e.target.value)}
                  />
                </div>
                <DialogFooter>
                  {!subscription && hasPaper && subscription != null ? (
                    <div className="w-full">
                      <UnlockButton />
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isAdding || paperAccountName.trim() === ""}
                      onClick={onAddPaperAccount}
                    >
                      {isAdding ? <LoadingSpinner /> : m.save()}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <DialogFooter className="flex flex-row justify-end space-x-2">
          {!subscription && hasExchange && subscription != null ? (
            <div className="w-full">
              <UnlockButton />
            </div>
          ) : !subscription && hasPaper && hasExchange ? (
            <></>
          ) : (
            <>
              <DialogClose className="items-start justify-start flex">
                <Button variant="outline">{m.cancel()}</Button>
              </DialogClose>
              <Link
                href={
                  selectedExchange === ""
                    ? ""
                    : `/settings/exchanges/${selectedExchange}`
                }
                className={
                  selectedExchange === ""
                    ? "cursor-not-allowed"
                    : "cursor-pointer"
                }
              >
                <Button disabled={selectedExchange === ""}>{m.next()}</Button>
              </Link>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <Card className="sm:max-w-[600px]">
        {!connectedKeys.filter(
          (e) => e.provider_id != null && e.key_status_name === "VALID",
        ).length && connectedKeys.length ? (
          <Alert
            className="relative shadow-sm  mx-auto mb-2"
            variant="destructive"
          >
            <span className="flex items-center">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle className="ml-2">{m.attention()}</AlertTitle>
            </span>
            <AlertDescription className="mt-2">
              {m.next_plain_florian_glow()}
            </AlertDescription>
          </Alert>
        ) : (
          <></>
        )}
        <CardHeader>
          <CardTitle>{m.orange_honest_ladybug_believe()}</CardTitle>
          <CardDescription>{m.novel_bland_hornet_leap()}</CardDescription>
        </CardHeader>

        <CardContent className="grid gap-4 py-4">
          <Input
            type="search"
            placeholder={m.search_for_exchange()}
            className="focus-visible:ring-0"
            onChange={(e) => setKeyword(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-auto">
            {!providers.length ? (
              [...Array(5)].map(() => (
                <Skeleton className="w-full h-[40px]" key={Math.random()} />
              ))
            ) : !filteredProviders.length ? (
              <p className="text-center my-4 text-sm col-span-2">
                {m.cozy_teal_tortoise_roam()} <b>{keyword}</b>
              </p>
            ) : (
              filteredProviders.map((provider) => {
                const isActive = selectedExchange === provider.name;
                return (
                  <Button
                    key={provider.id}
                    variant="outline"
                    className={`relative flex items-center justify-center space-x-2 ${
                      isActive ? "border-primary" : ""
                    }`}
                    onClick={() => handleExchangeSelect(provider.name)}
                  >
                    <ExchangeImage providerId={provider.id} width={24} />
                    <span>{capitalize(provider.name)}</span>
                    {isActive && (
                      <CircleCheck className="absolute right-2 top-1/2 -translate-y-1/2" />
                    )}
                  </Button>
                );
              })
            )}
          </div>
          <p className="text-center text-muted-foreground text-sm">
            {m.topical_major_camel_dream()}
          </p>
          <div className="flex justify-center">
            <Dialog open={open} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center justify-center space-x-2 md:w-1/2 self-center"
                >
                  <Image
                    src="/paper.svg"
                    alt="Paper Account Icon"
                    width={24}
                    height={24}
                  />
                  <span>{m.lime_crisp_llama_talk()}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{m.patchy_crazy_gazelle_boil()}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <Label
                    htmlFor="portfolio-name"
                    className="text-left min-w-fit"
                  >
                    {m.portfolio_name()}
                  </Label>
                  <Input
                    id="portfolio-name"
                    placeholder={m.soft_watery_fish_breathe()}
                    className="focus-visible:ring-0"
                    value={paperAccountName}
                    onChange={(e) => setPaperAccountName(e.target.value)}
                  />
                </div>
                <DialogFooter>
                  {!subscription && hasPaper && subscription != null ? (
                    <div className="w-full">
                      <UnlockButton />
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isAdding || paperAccountName.trim() === ""}
                      onClick={onAddPaperAccount}
                    >
                      {isAdding ? <LoadingSpinner /> : m.save()}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
        <CardFooter className="justify-end">
          {!subscription && hasExchange && subscription != null ? (
            <div className="w-full">
              <UnlockButton />
            </div>
          ) : (
            <Link
              href={
                selectedExchange === ""
                  ? ""
                  : `/settings/exchanges/${selectedExchange}`
              }
              className={
                selectedExchange === ""
                  ? "cursor-not-allowed"
                  : "cursor-pointer"
              }
            >
              <Button disabled={selectedExchange === ""}>{m.next()}</Button>
            </Link>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
